import type { FamilleDeDeclaration, Option } from '@taki/ui';

/**
 * LE VOCABULAIRE DU STUDIO, en memoire. Aucun SDK, aucun pont vers le depot.
 *
 * Ce fichier a porte un DECOR ecrit a la main, qui simulait la lecture de
 * l'app Inventaire avant que le pont de developpement lise vraiment le depot.
 * Le pont l'a remplace, et l'app est sortie du produit le 11/10/2026 : il ne
 * reste ici que les types et les tables d'options que l'ecran partage.
 *
 * LA REGLE QUI TIENT TOUT LE CHANTIER, et elle vaut deja ici : le studio
 * n'affiche QUE ce qu'il a lu du code, au moment du rendu. Il ne garde jamais
 * sa propre copie d'une app a cote du code, sous peine de refabriquer
 * `PROGRESS.md` : un fichier d'etat tenu a la main qui ment des la premiere
 * modification faite ailleurs. C'est pour ca que chaque element porte le
 * chemin du fichier d'ou il est lu (`source`).
 */

/* ------------------------------------------------------------------ */
/* Le vocabulaire                                                      */
/* ------------------------------------------------------------------ */

/**
 * La nature d'un element de la definition d'une app.
 *
 * Les six premieres valeurs sont les SECTIONS du Launcher, dans son vocabulaire
 * (son `brief/model.mjs`, demenage depuis dans
 * `packages/shell/src/dev/studio/brief/model.mjs`) : identite, metier, reglages,
 * liens, extra, demo. Les trois dernieres decrivent une porte et son contenu.
 */
export type StudioKind =
  | 'identite'
  | 'metier'
  /* LES JEUX DE DONNEES DE L'APP, declares une fois et partages par les portes
     qui les montrent. Il ne se confond pas avec `demo`, le jeu de DEMO. */
  | 'jeux'
  | 'reglage'
  | 'lien'
  | 'extra'
  | 'demo'
  | 'recap'
  /* LE PROMPT : la sortie du formulaire, le texte qu'on colle a l'agent. Comme
     `recap`, ce n'est pas une nature d'ELEMENT mais une entree de rail : aucune
     projection n'en produit jamais, et sa section ne rend donc aucune ligne
     (`packages/shell/src/dev/studio/brief-ui/PromptPreview.tsx`). */
  | 'prompt'
  | 'section-portes'
  | 'porte'
  | 'propriete'
  | 'champ'
  | 'vue'
  | 'notification'
  | 'intention';

/**
 * L'EDITEUR d'un element, quand l'ecran le rend en formulaire.
 *
 * `lecture` n'est pas un editeur desactive : c'est une valeur que le studio
 * AFFICHE mais n'ecrit jamais, parce qu'elle appartient a quelqu'un d'autre (la
 * version est ecrite par l'outil de release, l'identifiant est la clef de l'app
 * dans tout le depot). Un champ grise sans raison se lit comme un bug ; ces
 * champs-la portent donc toujours la leur.
 *
 * `collection` REPARE UNE FAUTE, et elle merite d'etre nommee. "Vues montees",
 * "Filtres", "Favoris", "Stats" ont d'abord ete rendus en CHAMPS TEXTE. C'etait
 * faux deux fois : leur valeur n'est pas une chaine mais un RESUME d'une liste
 * ("5 categories", "4 presets"), et rien n'empechait d'ecrire "7 categories"
 * sans qu'aucune categorie n'apparaisse. Un champ texte promet une ecriture
 * qu'il ne sait pas tenir. Ces lignes sont donc des PORTES vers leur liste :
 * elles montrent le resume, elles s'ouvrent, elles ne se tapent pas.
 */
export type StudioEditeur =
  | 'texte'
  | 'texte-long'
  | 'select'
  | 'switch'
  | 'cases'
  | 'date'
  | 'collection'
  /*
   * DES ETIQUETTES, PAS UNE LIGNE DE TEXTE. Le catalogue d'un champ a choix se
   * saisit valeur par valeur : chacune est un objet qu'on ajoute et qu'on
   * retire. Une ligne separee par des virgules obligeait a retaper le tout pour
   * en changer une, et la premiere valeur contenant une virgule coupait le
   * catalogue en deux sans prevenir.
   */
  | 'etiquettes'
  /*
   * UNE ICONE SE CHOISIT EN LA VOYANT, elle ne se tape pas : « on doit pouvoir
   * choisir l'icon, on tape pas le nom de l'icon en char brut ». Le choix visuel
   * existait sur une app NEUVE et pas sur une app livree, ou la meme valeur
   * restait un champ texte ou l'on ecrivait a l'aveugle. C'est le MEME composant
   * des deux cotes (`ChoixIcone`).
   *
   * CE QU'IL PORTE ICI EST LE NOM DU COMPOSANT (`IconCameraVideo`), et non la
   * clef du registre (`camera-video`) : cet editeur ne sort que de la projection
   * d'une FICHE, et `taki.app.json` exige le nom (« nom d'une icone @taki/ui
   * attendu », `scripts/check-app-entities.mjs`). Le choisisseur convertit au
   * bord, par sa prop `forme`.
   */
  | 'icone'
  /*
   * DEUX FACONS DE NE PAS ETRE MODIFIABLE, et elles ne se dessinent pas pareil.
   *
   * `lecture` = UNE VALEUR QUI VIENT DU CODE, au milieu de champs qui, eux,
   * s'ecrivent. Elle garde la FORME d'un champ pour que la ligne ne se dehanche
   * pas, mais elle n'est PAS grisee : un cadre blanc a encre pleine (arbitrage
   * du fondateur, 30/08). Un controle eteint invite a cliquer puis refuse, et
   * une colonne entiere de champs eteints se lit comme un ecran en panne ou en
   * attente de droits, alors que c'est de l'information. Le curseur reste
   * normal et la raison est dite sous le libelle.
   *
   * `info` = UNE VALEUR AFFICHEE, sur un ecran ou RIEN ne s'ecrit (le recap du
   * Dashboard). Sans cadre du tout : quand aucune ligne n'est modifiable, le
   * cadre ne distingue plus rien.
   */
  | 'info'
  | 'lecture';

/**
 * QUI SAIT APPLIQUER LE CHANGEMENT. C'est la distinction que l'ecran doit
 * rendre EVIDENTE, parce que c'est elle qui decide du geste de l'utilisateur :
 *  - `script` : le studio ecrit le fichier lui-meme, tout de suite, sans IA.
 *    Ajouter un champ, cocher une vue, poser un filtre : de la donnee a schema
 *    ferme, verifiee apres coup, annulable.
 *  - `agent` : le studio ne sait pas l'ecrire, il fabrique un texte a donner a
 *    un agent. Regles metier, canvas, et tous les MOTS de l'interface.
 */
export type StudioGeste = 'script' | 'agent';

/** Ou en est l'element, vis-a-vis du code reel. */
export type StudioEtat = 'lu' | 'attente' | 'a-coder';

/**
 * Les deux catalogues, avec leur TON. Le ton est le seul vocabulaire de
 * couleur partage par les apps (`neutral | accent | success | danger`), jamais
 * une couleur. Le lime du bouton d'ajout n'entre pas ici : une pastille de
 * statut ne se peint jamais en lime (regle G17).
 */
export const ETAT_OPTIONS: Option[] = [
  { value: 'lu', label: 'Lu du code', tone: 'success' },
  { value: 'attente', label: "Attend l'agent", tone: 'accent' },
  { value: 'a-coder', label: 'À coder', tone: 'neutral' },
];

export const GESTE_OPTIONS: Option[] = [
  { value: 'script', label: 'Script', tone: 'success' },
  { value: 'agent', label: 'Agent', tone: 'accent' },
];

/**
 * LES CINQ ROLES QUE LE SOCLE SAIT TRIER SANS COLONNE, avec leur mot.
 *
 * `sortValueOf` les lit directement sur la ligne projetee ; au-dela, une clef de
 * tri est une COLONNE. Le mot est ici, et pas dans la projection ni dans la
 * transformation, parce que les DEUX en ont besoin : l'une pour nommer la case
 * a cocher, l'autre pour ecrire le libelle qu'un role doit porter dans le
 * fichier. Deux tables auraient donne deux mots pour un role.
 */
export const ROLES_TRIABLES: { role: string; mot: string }[] = [
  { role: 'title', mot: 'Titre' },
  { role: 'subtitle', mot: 'Sous-titre' },
  { role: 'status', mot: 'Statut' },
  { role: 'dateStart', mot: 'Date' },
  { role: 'dateEnd', mot: 'Date de fin' },
];

/**
 * LES NEUF CALCULS D'UNE STAT, table FERMEE, avec le mot du formulaire.
 *
 * ELLE EST ICI POUR LA MEME RAISON QUE `ROLES_TRIABLES` juste au-dessus : les
 * DEUX bouts du studio en ont besoin. La projection y prend le mot de la liste
 * deroulante (`projectionPortes.ts`), et la transformation y prend la liste des
 * valeurs qu'elle accepte d'ECRIRE, pour ne pas poser dans le fichier un calcul
 * que le schema du socle refuserait (`ecransEdition.ts`). Deux tables auraient
 * donne deux verites sur ce qu'est un calcul.
 *
 * LE SCHEMA FAIT FOI, PAS CETTE TABLE : `calculDeStatSchema`
 * (`packages/data-model/src/app-screens.ts`) est rejoue a chaque ecriture.
 */
export const CALCULS_DE_STAT: Option[] = [
  { value: 'nb', label: 'nombre de fiches', tone: 'neutral' },
  { value: 'total', label: 'total', tone: 'neutral' },
  { value: 'moyenne', label: 'moyenne', tone: 'neutral' },
  { value: 'min', label: 'minimum', tone: 'neutral' },
  { value: 'max', label: 'maximum', tone: 'neutral' },
  { value: 'nbVaut', label: 'nombre pour une valeur', tone: 'neutral' },
  { value: 'nbDistinct', label: 'nombre de valeurs différentes', tone: 'neutral' },
  { value: 'nbRempli', label: 'nombre de fiches renseignées', tone: 'neutral' },
  { value: 'nbVide', label: 'nombre de fiches sans valeur', tone: 'neutral' },
];

/**
 * UNE LIGNE EN ATTENTE DE NOM, sur une porte : un favori ou une stat qu'on
 * vient d'ajouter et qui n'est pas encore ecrite.
 *
 * ELLE VIT DANS L'ETAT DE L'ECRAN, ET SES LIGNES DANS LA PROJECTION. C'est ce
 * decoupage qui evite un second vocabulaire : le catalogue de filtres d'un
 * favori neuf et le catalogue de champs d'une stat neuve sont ceux que la
 * projection calcule deja pour les existants. Les refabriquer dans l'ecran
 * aurait donne deux listes pour la meme question, et c'est toujours la seconde
 * qui ment.
 */
export interface EnAttenteDePorte {
  genre: 'favori' | 'stat';
  /** L'id de la porte qui la recevra. */
  porte: string;
  /** Un identifiant de LIGNE, pose par l'ecran ; jamais ecrit dans le fichier. */
  uid: string;
}

/**
 * CE QUE L'ECRAN DEMANDE A LA PROJECTION EN PLUS DE CE QU'IL LIT.
 *
 * `retirable` N'EST PAS UNE COQUETTERIE : la projection est PARTAGEE par les
 * deux studios, et « Retirer » n'a de sens que la ou il est cable. Le Studio du
 * modele (`dev/studio/StudioScreen.tsx`) passe bien un `onSupprimer`, mais il
 * ne connait ni les favoris ni les stats : un bouton rendu la-bas se serait
 * contente de ne rien faire, en silence. Il ne demande donc pas ces lignes, et
 * il ne les recoit pas.
 */
export interface OptionsDeProjection {
  /** Le catalogue SDK lu par l'hôte, pour borner les champs natifs de chaque porte. */
  familles?: readonly FamilleDeDeclaration[];
  /** Les lignes qui se retirent portent leur geste (Studio d'une organisation). */
  retirable?: boolean;
  /** Ce qui attend un nom, et que la projection doit rendre en plus du fichier. */
  enAttente?: readonly EnAttenteDePorte[];
  /**
   * LA FAMILLE D'UN JEU NE SE CHOISIT PAS ICI (Studio d'une organisation).
   *
   * C'est le curseur du regime declare : les familles sont le vocabulaire du
   * SOCLE, un client les MONTRE, il n'en invente pas. Son moteur d'edition ne
   * transporte d'ailleurs aucune famille (`familles: new Map()`,
   * `client/edition.ts`), donc la liste deroulante etait un controle qui
   * comptait dans « Enregistrer (n) » et n'ecrivait rien. La ligne reste, en
   * LECTURE, avec sa raison : c'est un fait de l'app, pas un trou.
   */
  familleFigee?: boolean;
}

/**
 * CE QU'UNE LIGNE PORTE QUAND ELLE SE RETIRE, et rien quand elle ne se retire
 * pas. Le geste lui-meme est rendu par `Formulaire`, et seulement si l'ecran
 * lui a donne un `onSupprimer` : les deux conditions se tiennent, de sorte
 * qu'aucun studio ne rende un « Retirer » qu'il ne cable pas.
 *
 * ELLE VIT ICI, A COTE DES OPTIONS QU'ELLE LIT, parce que les DEUX modules de
 * projection s'en servent (les favoris et les stats d'une porte, les sections
 * d'une fiche) : ecrite deux fois, elle aurait fini par dire deux choses.
 */
export const retirable = (options: OptionsDeProjection): { supprimable?: true } =>
  options.retirable === true ? { supprimable: true } : {};

/** Le libelle d'une valeur dans un catalogue, ou la valeur brute si inconnue. */
export function labelOf(options: Option[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

/* ------------------------------------------------------------------ */
/* L'element                                                           */
/* ------------------------------------------------------------------ */

/**
 * OU UNE LIGNE TOMBE DANS UN TABLEAU : quel tableau, quelle ligne, quelle
 * colonne. Trois clefs de DONNEE, posees par la projection.
 */
export interface StudioTableau {
  /**
   * QUEL TABLEAU. Une sous-section peut en porter plusieurs : « 4. Detail par
   * vue » en rend un par vue montee, plus celui du catalogue de colonnes.
   */
  clef: string;
  /**
   * LA LEGENDE AU-DESSUS DU CADRE. Absente quand le titre de la sous-section
   * suffit (un seul tableau dedans) ; presente des qu'il y en a plusieurs, sans
   * quoi on ne sait pas de quoi chacun parle.
   */
  titre?: string;
  /** L'en-tete de la colonne d'IDENTITE, la premiere. */
  entete: string;
  /** QUELLE LIGNE de ce tableau. Les lignes sortent dans l'ordre d'arrivee. */
  ligne: string;
  /**
   * CE QUE LA CELLULE D'IDENTITE AFFICHE quand aucun controle ne l'occupe : le
   * mot du role, le libelle d'une colonne. Une ligne dont la colonne
   * `identite` porte un controle (le libelle d'un champ, le titre d'une carte)
   * n'en a pas besoin.
   */
  mot?: string;
  /**
   * LA CLEF TECHNIQUE, en mono sous le mot : celle qui est ecrite dans le
   * fichier ou sur chaque document, donc la seule chose de la ligne qui ne se
   * change pas. Absente quand elle serait le mot lui-meme.
   */
  clefLigne?: string;
  /**
   * QUELLE COLONNE, prise dans `COLONNES_TABLEAU`. La valeur reservee
   * `identite` designe la premiere colonne, celle qui nomme la ligne.
   */
  colonne: string;
}

/**
 * LE VOCABULAIRE ORDONNE DES COLONNES, et c'est LUI qui fixe l'ordre.
 *
 * POURQUOI PAS L'ORDRE D'EMISSION : un champ sans catalogue n'emet pas sa
 * cellule « Valeurs », un role non modifiable n'emet pas sa case. L'ordre des
 * colonnes sauterait donc d'une app a l'autre, et d'une vue a l'autre dans la
 * meme sous-section.
 *
 * UNE COLONNE EXISTE SI ET SEULEMENT SI le tableau a emis au moins une de ses
 * cellules. C'est ce qui fait que les colonnes DIFFERENT d'une vue a l'autre
 * (« Modifiable » n'apparait que sur le kanban et la fiche, « Mot affiche »
 * que sur le tableau) sans une seule condition dans l'ecran.
 *
 * `largeur` est un INDICE de geometrie, pas une contrainte : le tableau se pose
 * en `table-auto`, une cellule plus large elargit sa colonne.
 */
export interface ColonneTableau {
  clef: string;
  /** L'en-tete de la colonne. */
  mot: string;
  /** La classe de largeur ; vide = la colonne prend ce qui reste. */
  largeur?: string;
  /** Ce qu'on lit dans une cellule que la projection n'a pas emise. */
  vide?: string;
  /** Cette colonne rend aussi l'aide de sa cellule, sous le controle. */
  aide?: boolean;
}

export const COLONNES_TABLEAU: ColonneTableau[] = [
  /* Les champs declares d'un jeu de donnees. */
  { clef: 'type', mot: 'Type', largeur: 'w-[11rem]', vide: '-' },
  { clef: 'valeurs', mot: 'Valeurs', vide: 'sans catalogue' },
  /* La cible d'un champ `lien` : quatre colonnes, qui n'existent que si un lien
     est declare (regle « une colonne existe si une cellule l'a emise »). */
  { clef: 'cible', mot: 'Cible', largeur: 'w-[12rem]', vide: '-' },
  { clef: 'typecible', mot: 'Type visé', largeur: 'w-[10rem]', vide: '-' },
  { clef: 'titrecible', mot: 'Champ du nom', largeur: 'w-[11rem]', vide: '-' },
  { clef: 'choixcible', mot: 'Choix proposés', vide: '-' },
  { clef: 'requis', mot: 'Obligatoire', largeur: 'w-[9rem]', vide: '-' },
  { clef: 'exemple', mot: 'Exemple', vide: '-' },
  /* Le detail d'une vue : un role, le champ qui le tient, et ce qui s'ecrit. */
  { clef: 'champ', mot: 'Champ', vide: '-', aide: true },
  { clef: 'modifiable', mot: 'Modifiable', largeur: 'w-[10rem]', vide: '-' },
  { clef: 'mot', mot: 'Mot affiché', largeur: 'w-[13rem]', vide: '-' },
  /* Le catalogue de colonnes de l'ecran. */
  { clef: 'total', mot: 'Total', largeur: 'w-[11rem]', vide: '-' },
  { clef: 'tri', mot: 'Tri', largeur: 'w-[11rem]', vide: '-' },
  /*
   * LES CARTES DE LA FICHE : UN TABLEAU PAR SECTION, donc UNE seule colonne de
   * valeurs. Le fondateur a redemande le decoupage (« fait un tableau également
   * par section »), et le modele est « Detail par vue », qui rend un tableau par
   * vue. Chaque section a donc son cadre, ou ses reglages sont les LIGNES : le
   * titre, la position, la disposition, les champs. La rangee de cases des
   * champs y prend enfin toute la largeur, la ou elle se tassait dans une
   * colonne partagee par quatre sections.
   */
  { clef: 'valeur', mot: 'Valeur', vide: '-' },
];

/**
 * OU MENE UNE LIGNE, ET CE QU'ELLE SAIT CONFIER, en DONNEE.
 *
 * POURQUOI CETTE FORME. Une ligne de « Reste a remplir » dit une phrase
 * (« Porte 1 (Prout) : Liste : aucun champ ne fait le titre. ») et porte deux
 * gestes : y aller, et confier la sous-section a l'agent. Les deux ont besoin de
 * savoir QUELLE porte et QUELLE sous-section. Les tirer de la phrase demanderait
 * de la decouper, c'est-a-dire de refaire la panne qui a deja envoye tout champ
 * neuf dans le mauvais jeu de donnees, en silence, sur une CI verte. La source
 * (`verrousDeSection`) rend donc sa cible en donnee, et l'ecran la RESOUT ici,
 * une fois : l'entree de rail a ouvrir, l'ancre ou defiler, et la section a
 * cocher. Rien ne se recalcule au moment du clic.
 */
export interface CibleDeLigne {
  /** L'entree de rail a ouvrir : une section d'app, ou `porte:<uid>`. */
  entree: string;
  /**
   * L'ancre `data-zone` de la sous-section ou defiler apres l'ouverture.
   * `null` quand la ligne ne vise aucune sous-section (les verrous de racine).
   */
  ancre: string | null;
  /**
   * LA SECTION DELEGUABLE que le raccourci coche, l'identifiant du catalogue
   * (`brief/derive.mjs`). `null` quand rien ne se confie a ce niveau : la ligne
   * ne porte alors pas le geste, plutot qu'un bouton qui n'ecrirait rien.
   */
  section: string | null;
  /** L'uid de la porte qui PORTE cette section. `null` : elle vit a la racine. */
  porte: string | null;
}

/**
 * UN ELEMENT de la definition d'une app : une porte, un champ, une vue, ou une
 * intention de changement. Une seule forme pour les quatre, comme l'app
 * Inventaire tient trois portes sur une seule famille : les portes du studio
 * tranchent le meme jeu par `kind`, elles ne chargent pas quatre collections.
 */
export interface StudioItem {
  id: string;
  kind: StudioKind;
  /** Le nom court, tel qu'il s'affiche en titre de ligne. */
  nom: string;
  /** La phrase qui detaille le nom : le sous-titre des vues. */
  detail: string;
  geste: StudioGeste;
  etat: StudioEtat;
  /** La forme exacte : le type d'un champ, le type d'une vue, la nature d'une porte. */
  forme: string;
  /**
   * LE FICHIER D'OU CA SERAIT LU. C'est la colonne qui dit au fondateur d'ou
   * vient ce qu'il regarde, et c'est le contrat du studio : ce qu'il montre, il
   * peut le montrer dans le code. Un element sans source serait un element
   * invente, donc un mensonge.
   */
  source: string;
  /** L'ordre d'affichage a l'interieur d'une porte, quand aucun tri n'est pose. */
  ordre: number;
  /** Comment l'ecran rend ce champ en formulaire. Absent : pas de formulaire. */
  editeur?: StudioEditeur;
  /** Le catalogue, pour `editeur: 'select'` UNIQUEMENT. */
  options?: Option[];
  /** L'aide sous le champ : ce qu'on attend, ou pourquoi il ne s'edite pas. */
  aide?: string;
  /** Le champ prend toute la largeur du formulaire (texte long). */
  large?: boolean;
  /**
   * L'element auquel celui-ci APPARTIENT : l'id de la porte, pour ses
   * proprietes. Absent partout ailleurs, une section d'app n'ayant pas de
   * parent.
   */
  parent?: string;
  /**
   * Le texte du champ VIDE. Il ne decrit pas le champ (c'est le travail de
   * `aide`), il montre une reponse PLAUSIBLE : sur une app qui n'existe pas
   * encore, six cadres vides ne disent pas ce qu'on attend d'eux.
   */
  placeholder?: string;
  /**
   * LE SOUS-GROUPE dans lequel le champ vit, a l'interieur de sa section.
   *
   * Il porte le decoupage du Launcher la ou il en a un : une PORTE s'y edite en
   * huit sous-sections (Porte, Jeu de donnees, Vues, Detail par vue, Fiche,
   * Filtres et tri, Actions, Stats), et les rejouer a plat perdrait l'ordre de
   * remplissage que ce formulaire enseigne. Absent : le champ vit directement
   * sous sa section.
   */
  groupe?: string;
  /** Le champ est obligatoire : le libelle porte une marque. */
  requis?: boolean;
  /**
   * CETTE LIGNE EST UNE CELLULE DE TABLEAU, et ce descripteur dit LAQUELLE.
   *
   * POURQUOI UN TABLEAU. Un objet declare occupe plusieurs lignes parce qu'il
   * porte plusieurs decisions : un champ en occupe quatre (libelle, type,
   * catalogue, obligatoire), un role deux (le champ qui le tient, s'il se
   * modifie), une carte de fiche quatre (titre, position, disposition, champs).
   * Empilees en formulaire, elles noient la sous-section : « 4. Detail par
   * vue » rendait 49 lignes, « 4. Fiche » 17, chacune repetant le nom de
   * l'objet dans son libelle. On ne voyait plus le tableau qu'elles FORMENT
   * deja.
   *
   * POURQUOI LE DECOUPAGE EST UNE DONNEE ET JAMAIS UN DECOUPAGE D'ID NI DE
   * LIBELLE. Le lien entre une cellule et sa ligne ne se devine pas : le
   * deduire du texte affiche est exactement la faute qui a envoye tout champ
   * neuf dans le mauvais jeu, en silence, sur un fichier valide et une CI
   * verte. La projection POSE le lien, l'ecran le lit.
   *
   * LES IDS NE CHANGENT PAS. Chaque cellule ecrit sous l'id que la projection
   * lui a donne : le routage de `appliquer`, `champsEdition` et
   * `ecransEdition` ne sait pas que le rendu a change.
   */
  tableau?: StudioTableau;
  /**
   * LE JEU DE DONNEES AUQUEL CETTE LIGNE APPARTIENT, sous la forme
   * `famille::type` telle que le fichier de champs la nomme.
   *
   * POURQUOI CETTE CLEF EXISTE : l'ecran doit savoir DANS QUEL JEU ecrire un
   * champ neuf. Il le devinait en redecoupant le TEXTE AFFICHE de la ligne
   * « Jeu de donnees », ce qui a casse a la seconde ou ce texte a change : la
   * comparaison ne retombait plus jamais juste, et tout champ neuf partait dans
   * le PREMIER jeu, en silence, sur un fichier valide et une CI verte.
   *
   * Un lien entre deux choses est une DONNEE. Le deduire d'un libelle, c'est le
   * casser au premier mot qu'on change.
   */
  jeuClef?: string;
  /**
   * L'ELEMENT SE RETIRE. Vrai UNIQUEMENT sur ce qui a ete ajoute a la main :
   * ce que le studio a LU du code ne se supprime pas depuis un formulaire, ca
   * se supprime dans le code. La croix ne s'affiche donc que la ou elle tient
   * sa promesse.
   */
  supprimable?: boolean;
  /**
   * UN SECOND GESTE SUR LA LIGNE, avec son mot.
   *
   * La ligne d'un champ qui manque en porte deux : aller le remplir (le
   * controle `collection`), ou le confier a l'agent. Le second n'a pas de mot
   * fixe -- il dit ce qu'il fait, et il change selon ce qui est delegable --
   * donc il vit ici plutot que dans une prop de plus du formulaire.
   *
   * ABSENT : la ligne n'a qu'un geste, comme partout ailleurs.
   */
  geste2?: { libelle: string; titre?: string };
  /**
   * CE QUE LA LIGNE MET DANS LE PRESSE-PAPIER, quand sa valeur n'existe que
   * pour etre collee ailleurs.
   *
   * LE SEUL CAS AUJOURD'HUI est le chemin d'un projet du Studio : « il n'y a
   * AUCUN bouton copier, pour un chemin dont le seul usage est d'etre colle ».
   * C'est une PROP a part et non un `geste2`, parce que copier a besoin de dire
   * si ca a marche (« Copié », « Copie refusée ») : une copie qui echoue est
   * silencieuse, et un bouton muet se lit comme s'il avait reussi.
   *
   * ABSENT : la ligne ne porte pas de bouton copier.
   */
  copier?: string;
  /**
   * LA FAMILLE DU JEU QU'UNE LIGNE DE PORTE MONTRE, pour la mention des familles que Lia et
   * Flux ne voient pas (`ouvertures-ia.ts`). Posee par celui qui fabrique la ligne de la
   * liste des portes ; `Formulaire` la rend a cote du nom. Absente : rien.
   */
  familleDePorte?: string;
  /**
   * OU LA LIGNE MENE, quand elle mene quelque part. Pose par celui qui fabrique
   * la ligne, lu par les deux gestes ; absente, la ligne ne bouge de nulle part.
   */
  cible?: CibleDeLigne;
}
